export class TrixToolbarA11y {
  static ITEM_SELECTOR = [
    '.trix-button-row .trix-button:not([disabled])',
    '.trix-button-row .trix-header-select',
  ].join(',');

  static PICKER_ITEM_SELECTOR = '.trix-dialog .trix-picker-item';

  static isVisible(el) {
    return el.checkVisibility();
  }

  // Trix wires action/attribute handlers to mousedown, not click — synthesize
  // both so keyboard activation triggers the underlying handler.
  static synthClick(el) {
    el.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
    el.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
  }

  constructor(toolbar, trixField) {
    this.toolbar = toolbar;
    this.trixField = trixField;
    this.pendingDialogToEnter = null;
    this.onFocusIn = this.onFocusIn.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onTrixFocus = this.onTrixFocus.bind(this);
    this.onDialogMutation = this.onDialogMutation.bind(this);
  }

  init() {
    if (!this.toolbar || this.toolbar.dataset.a11yReady) return;

    // Mark toolbar so init() is idempotent (event re-fires won't re-attach listeners).
    this.toolbar.dataset.a11yReady = 'true';
    // WAI-ARIA toolbar pattern: container needs role=toolbar so screen readers
    // announce it and may auto-enter focus mode when entering the group.
    this.toolbar.setAttribute('role', 'toolbar');

    this.refreshTabStops();
    this.observeDialogs();

    this.toolbar.addEventListener('focusin', this.onFocusIn);
    this.toolbar.addEventListener('keydown', this.onKeyDown, true);

    if (this.trixField) {
      this.trixField.addEventListener('trix-focus', this.onTrixFocus);
    }
  }

  destroy() {
    this.toolbar.removeEventListener('focusin', this.onFocusIn);
    this.toolbar.removeEventListener('keydown', this.onKeyDown, true);
    if (this.trixField) {
      this.trixField.removeEventListener('trix-focus', this.onTrixFocus);
    }
    if (this.dialogObserver) this.dialogObserver.disconnect();
    delete this.toolbar.dataset.a11yReady;
  }

  get items() {
    return Array.from(this.toolbar.querySelectorAll(TrixToolbarA11y.ITEM_SELECTOR))
      .filter(TrixToolbarA11y.isVisible);
  }

  refreshTabStops() {
    const items = this.items;
    if (!items.length) return;

    let active = items.find(el => el.tabIndex === 0);
    if (!active) active = items[0];

    items.forEach(el => {
      el.setAttribute('tabindex', el === active ? '0' : '-1');
    });
  }

  onTrixFocus() {
    this.refreshTabStops();
  }

  onFocusIn(event) {
    // Mouse-click or programmatic focus on a toolbar item: move the single
    // tab stop (tabindex=0) to that item; demote the previous one to -1.
    const target = event.target.closest(TrixToolbarA11y.ITEM_SELECTOR);
    if (!target) return;
    const previous = this.toolbar.querySelector(
      TrixToolbarA11y.ITEM_SELECTOR.split(',').map(s => `${s}[tabindex="0"]`).join(',')
    );
    if (previous && previous !== target) previous.setAttribute('tabindex', '-1');
    target.setAttribute('tabindex', '0');
  }

  onKeyDown(event) {
    const current = document.activeElement;
    if (current && current.matches && current.matches(TrixToolbarA11y.PICKER_ITEM_SELECTOR)) {
      return this.onPickerKeyDown(event, current);
    }

    const items = this.items;
    const index = items.indexOf(current);
    if (index === -1) return;

    let next = null;
    switch (event.key) {
      case 'ArrowRight':
        next = items[(index + 1) % items.length];
        break;
      case 'ArrowLeft':
        next = items[(index - 1 + items.length) % items.length];
        break;
      case 'Home':
        next = items[0];
        break;
      case 'End':
        next = items[items.length - 1];
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.armDialogEntry(current);
        TrixToolbarA11y.synthClick(current);
        return;
      default:
        return;
    }

    event.preventDefault();
    event.stopPropagation();
    if (next) {
      items.forEach(el => el.setAttribute('tabindex', el === next ? '0' : '-1'));
      next.focus();
    }
  }

  // Keyboard handling for picker items inside open dialogs (alignment, heading):
  // Arrow keys move between options, Enter/Space activates the option, Escape
  // closes the dialog and returns focus to the opener button.
  onPickerKeyDown(event, current) {
    const dialog = current.closest('.trix-dialog');
    if (!dialog) return;

    const items = Array.from(dialog.querySelectorAll('.trix-picker-item'))
      .filter(TrixToolbarA11y.isVisible);
    const index = items.indexOf(current);

    let next = null;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = items[(index + 1) % items.length];
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = items[(index - 1 + items.length) % items.length];
        break;
      case 'Home':
        next = items[0];
        break;
      case 'End':
        next = items[items.length - 1];
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        TrixToolbarA11y.synthClick(current);
        this.returnFocusToOpener(dialog);
        return;
      case 'Escape':
        event.preventDefault();
        this.closeDialogAndReturnFocus(dialog);
        return;
      default:
        return;
    }

    event.preventDefault();
    event.stopPropagation();
    if (next) {
      items.forEach(el => el.setAttribute('tabindex', el === next ? '0' : '-1'));
      next.focus();
    }
  }

  // Before activating a toolbar button, mark the matching dialog (if any) as
  // pending. The dialog MutationObserver will move focus into it once Trix
  // removes the .hidden class.
  armDialogEntry(button) {
    const action = button.getAttribute && button.getAttribute('data-trix-action');
    if (!action) return;
    const dialog = this.toolbar.querySelector(`[data-trix-dialog="${action}"]`);
    if (dialog) this.pendingDialogToEnter = dialog;
  }

  focusFirstPickerItem(dialog) {
    const pickerItems = Array.from(dialog.querySelectorAll('.trix-picker-item'))
      .filter(TrixToolbarA11y.isVisible);
    if (!pickerItems.length) return;
    pickerItems.forEach((el, i) => el.setAttribute('tabindex', i === 0 ? '0' : '-1'));
    pickerItems[0].focus();
  }

  openerFor(dialog) {
    const name = dialog.getAttribute('data-trix-dialog');
    return name ? this.toolbar.querySelector(`[data-trix-action="${name}"]`) : null;
  }

  returnFocusToOpener(dialog) {
    const opener = this.openerFor(dialog);
    if (opener) opener.focus();
  }

  closeDialogAndReturnFocus(dialog) {
    const opener = this.openerFor(dialog);
    if (!opener) return;
    // Re-clicking the opener toggles the dialog closed (see TrixAlignment#onClick).
    TrixToolbarA11y.synthClick(opener);
    opener.focus();
  }

  // Mirror dialog open/closed state onto the opener's aria-expanded so screen
  // readers announce the popup state for mouse and keyboard interactions alike.
  observeDialogs() {
    const dialogs = this.toolbar.querySelectorAll('[data-trix-dialog]');
    if (!dialogs.length) return;
    this.dialogObserver = new MutationObserver(this.onDialogMutation);
    dialogs.forEach(d => this.dialogObserver.observe(d, {
      attributes: true,
      attributeFilter: ['class'],
    }));
  }

  onDialogMutation(mutations) {
    mutations.forEach(m => {
      const dialog = m.target;
      const isOpen = !dialog.classList.contains('hidden');

      const opener = this.openerFor(dialog);
      if (opener && opener.hasAttribute('aria-haspopup')) {
        opener.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      }

      if (isOpen && this.pendingDialogToEnter === dialog) {
        this.pendingDialogToEnter = null;
        this.focusFirstPickerItem(dialog);
      }
    });
  }
}
